import React from 'react';
import PropTypes from 'prop-types';
import { LuCalendarPlus, LuChevronDown, LuInfo, LuLoaderCircle, LuPlus, LuSave, LuX } from 'react-icons/lu';
import { INPUT_CLASS } from '../../../../../components/form/FormControls';
import { useTranslation } from '../../../../../i18n/LanguageContext';
import { BLOCK_TITLE } from '../../../components/ProfileUi';
import { ADD, optionRect } from '../stageLayout';
import { PROJECTS, dateOfCell, visitById } from '../mockData';
import CalendarScene from './CalendarScene';

const abs = (rect) => ({ left: rect.x, top: rect.y, width: rect.w, height: rect.h });
const VISIT_TYPES = ['hastane', 'home', 'telefon', 'unplanning', 'handover'];
const PATIENTS = ['P-1042', 'P-1120', 'P-1150'];
const PROJECT_KEYS = ['onko', 'kardiyo', 'noro'];

/** A form control as drawn: the value (or placeholder), focus ring, chevron. */
const Control = ({ rect, value, placeholder, focused, select }) => (
  // The wrapper carries the size: INPUT_CLASS has w-full, which beats an inline width
  <span style={abs(rect)} className="tw-absolute">
    <span className={`tw-flex tw-items-center tw-justify-between ${INPUT_CLASS} tw-transition-shadow tw-duration-300 ${focused ? 'tw-ring-2 tw-ring-teal-500/50' : ''}`}>
      <span className={`tw-truncate ${value ? 'tw-text-slate-800 dark:tw-text-slate-100' : 'tw-text-slate-400'}`}>{value || placeholder}</span>
      {select && <LuChevronDown aria-hidden="true" className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-slate-400" />}
    </span>
  </span>
);

Control.propTypes = { rect: PropTypes.object.isRequired, value: PropTypes.string, placeholder: PropTypes.string, focused: PropTypes.bool, select: PropTypes.bool };

const FieldLabel = ({ rect, children, required }) => (
  <span style={{ left: rect.x + 2, top: rect.y - 21 }} className="tw-absolute tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">
    {children}
    {required && <span className="tw-ml-1 tw-text-rose-500">*</span>}
  </span>
);

FieldLabel.propTypes = { rect: PropTypes.object.isRequired, children: PropTypes.node, required: PropTypes.bool };

/** An open select list under a field. */
const Menu = ({ rect, options, hover }) => (
  <div style={{ left: rect.x, top: rect.y + rect.h + 4, width: rect.w }} className="tw-absolute tw-z-30 tw-overflow-hidden tw-rounded-xl tw-border tw-border-white/80 tw-bg-white tw-py-1 tw-shadow-2xl dark:tw-border-white/10 dark:tw-bg-[#262a34]">
    {options.map((option, index) => (
      <span
        key={option}
        style={{ height: optionRect(rect, index).h }}
        className={`tw-mx-1 tw-flex tw-items-center tw-rounded-lg tw-px-3 tw-text-sm ${index === hover ? 'tw-bg-teal-500/15 tw-font-semibold tw-text-teal-800 dark:tw-text-teal-200' : 'tw-text-slate-700 dark:tw-text-slate-200'}`}
      >
        {option}
      </span>
    ))}
  </div>
);

Menu.propTypes = { rect: PropTypes.object.isRequired, options: PropTypes.arrayOf(PropTypes.string).isRequired, hover: PropTypes.number };

const Toggle = ({ rect, label, on }) => (
  <span style={abs(rect)} className={`tw-absolute tw-flex tw-items-center tw-justify-between tw-rounded-xl tw-border tw-border-white tw-bg-white/70 tw-px-3.5 tw-text-sm tw-font-semibold tw-shadow-sm dark:tw-border-white/10 dark:tw-bg-white/[0.05] ${on ? 'tw-text-teal-800 dark:tw-text-teal-200' : 'tw-text-slate-600 dark:tw-text-slate-300'}`}>
    <span className="tw-truncate">{label}</span>
    <span className={`tw-relative tw-h-5 tw-w-9 tw-rounded-full tw-transition-colors tw-duration-300 ${on ? 'tw-bg-teal-500' : 'tw-bg-slate-300 dark:tw-bg-white/15'}`}>
      <span className={`tw-absolute tw-top-0.5 tw-h-4 tw-w-4 tw-rounded-full tw-bg-white tw-shadow-sm tw-transition-[left] tw-duration-300 ${on ? 'tw-left-[1.125rem]' : 'tw-left-0.5'}`} />
    </span>
  </span>
);

Toggle.propTypes = { rect: PropTypes.object.isRequired, label: PropTypes.string.isRequired, on: PropTypes.bool };

/**
 * "Yeni Vizit" over the calendar. `state.appear` (0–1) fades the dialog in;
 * field values, open lists and the saving spinner come from the timeline.
 */
const AddVisitScene = ({ state }) => {
  const { t, locale } = useTranslation();
  const label = (key, vars) => t(`profilePage.calendar.visitForm.${key}`, vars);
  const { appear = 0, focus, menu, menuHover, project, patient, date, windowSet, type, number, repeat, saving } = state;
  const visit = visitById('N');
  const base = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  const day = (col) => dateOfCell(base, visit.row, col);
  const short = (value) => value.toLocaleDateString(locale);

  return (
    <>
      <CalendarScene state={state.calendar || {}} />
      {appear > 0 && (
        <>
          <div style={{ opacity: appear }} className="tw-absolute tw-inset-0 tw-bg-slate-950/45 dark:tw-bg-black/60" />
          <div
            style={{ ...abs(ADD.dialog), opacity: appear, transform: `translateY(${(1 - appear) * 24}px) scale(${0.97 + appear * 0.03})` }}
            className="tw-absolute tw-overflow-hidden tw-rounded-[1.75rem] tw-border tw-border-white/80 tw-bg-white tw-shadow-2xl dark:tw-border-white/10 dark:tw-bg-[#1f232b]"
          />
          <div style={{ opacity: appear }} className="tw-absolute tw-inset-0">
            {/* Başlık */}
            <span style={{ left: 264, top: 66 }} className="tw-absolute tw-grid tw-h-9 tw-w-9 tw-place-items-center tw-rounded-xl tw-bg-gradient-to-br tw-from-teal-400 tw-to-cyan-600 tw-text-white">
              <LuCalendarPlus className="tw-h-[18px] tw-w-[18px]" />
            </span>
            <span style={{ left: 312, top: 62 }} className="tw-absolute tw-text-lg tw-font-semibold tw-text-slate-900 dark:tw-text-white">{label('title')}</span>
            <span style={{ left: 312, top: 88 }} className="tw-absolute tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{label('subtitle')}</span>
            <span style={abs(ADD.close)} className="tw-absolute tw-grid tw-place-items-center tw-rounded-full tw-text-slate-500"><LuX className="tw-h-[18px] tw-w-[18px]" /></span>
            <span style={{ left: 240, top: 118, width: 800 }} className="tw-absolute tw-h-px tw-bg-slate-200 dark:tw-bg-white/10" />

            {/* Proje ve hasta */}
            <span style={{ left: 264, top: 128 }} className={`tw-absolute ${BLOCK_TITLE}`}>{label('sections.who')}</span>
            <FieldLabel rect={ADD.project} required>{label('project')}</FieldLabel>
            <Control rect={ADD.project} select value={project ? PROJECTS[project].name : ''} placeholder={label('chooseProject')} focused={focus === 'project'} />
            <FieldLabel rect={ADD.patient}>{label('patient')}</FieldLabel>
            <Control rect={ADD.patient} select value={patient || ''} placeholder={project ? label('patientOptional') : label('chooseProjectFirst')} focused={focus === 'patient'} />
            <span style={abs(ADD.addPatient)} className={`tw-absolute tw-grid tw-place-items-center tw-rounded-full tw-border tw-border-white tw-bg-white/80 tw-text-teal-700 tw-shadow-sm dark:tw-border-white/10 dark:tw-bg-white/[0.06] dark:tw-text-teal-300 ${focus === 'addPatient' ? 'tw-ring-2 tw-ring-teal-500/50' : ''}`}>
              <LuPlus className="tw-h-4 tw-w-4" />
            </span>

            {/* Tarih ve pencere */}
            <span style={{ left: 264, top: 230 }} className={`tw-absolute ${BLOCK_TITLE}`}>{label('sections.when')}</span>
            <FieldLabel rect={ADD.date} required>{label('visitDate')}</FieldLabel>
            <Control rect={ADD.date} value={date ? short(day(visit.mark)) : ''} placeholder={t('profilePage.tutorial.stage.datePlaceholder')} focused={focus === 'date'} />
            <FieldLabel rect={ADD.windowStart} required>{label('windowStart')}</FieldLabel>
            <Control rect={ADD.windowStart} value={windowSet ? short(day(visit.start)) : ''} placeholder={t('profilePage.tutorial.stage.datePlaceholder')} />
            <FieldLabel rect={ADD.windowEnd} required>{label('windowEnd')}</FieldLabel>
            <Control rect={ADD.windowEnd} value={windowSet ? short(day(visit.end)) : ''} placeholder={t('profilePage.tutorial.stage.datePlaceholder')} />
            <span style={{ left: 264, top: 330 }} className="tw-absolute tw-text-xs tw-font-semibold tw-text-slate-500 dark:tw-text-slate-400">{label('quickWindow')}</span>
            {[0, 1, 3, 7].map((days, index) => (
              <span
                key={days}
                style={{ left: 440 + index * 87, top: ADD.quick.y, width: 80, height: 30 }}
                className={`tw-absolute tw-grid tw-place-items-center tw-rounded-full tw-border tw-text-xs tw-font-semibold ${
                  days === 3 && focus === 'quick3' ? 'tw-border-teal-400 tw-bg-teal-500/10 tw-text-teal-700 dark:tw-text-teal-300' : 'tw-border-slate-200 tw-bg-white/70 tw-text-slate-700 dark:tw-border-white/10 dark:tw-bg-white/[0.04] dark:tw-text-slate-200'
                }`}
              >
                {days === 0 ? label('sameDay') : label('plusMinus', { n: days })}
              </span>
            ))}
            {windowSet && (
              <div style={abs(ADD.summary)} className="tw-absolute tw-flex tw-items-start tw-gap-2.5 tw-rounded-2xl tw-bg-teal-500/[0.08] tw-px-3.5 tw-py-2.5 tw-text-sm tw-text-teal-900 dark:tw-text-teal-100">
                <LuInfo className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0" />
                <div>
                  <p className="tw-font-semibold tw-capitalize">{day(visit.mark).toLocaleDateString(locale, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                  <p className="tw-text-xs tw-opacity-80">{label('windowSummary', { before: 3, after: 3, total: 6 })}</p>
                </div>
              </div>
            )}

            {/* Vizit bilgileri */}
            <span style={{ left: 264, top: 432 }} className={`tw-absolute ${BLOCK_TITLE}`}>{label('sections.details')}</span>
            <FieldLabel rect={ADD.type}>{label('visitType')}</FieldLabel>
            <Control rect={ADD.type} select value={type ? t(`profilePage.calendar.visitTypes.${type}`) : ''} placeholder={label('choose')} focused={focus === 'type'} />
            <FieldLabel rect={ADD.number}>{label('visitNumber')}</FieldLabel>
            <Control rect={ADD.number} value={number || ''} placeholder={label('visitNumberPlaceholder')} focused={focus === 'number'} />
            <span style={{ left: ADD.number.x + 2, top: ADD.number.y + 46, width: ADD.number.w }} className="tw-absolute tw-text-xs tw-leading-snug tw-text-slate-500 dark:tw-text-slate-400">
              {number ? label('suggestedNext', { n: number }) : label('visitNumberHint')}
            </span>

            {/* Seçenekler */}
            <span style={{ left: 264, top: 540 }} className={`tw-absolute ${BLOCK_TITLE}`}>{label('sections.options')}</span>
            <Toggle rect={ADD.repeat} label={label('repeat')} on={Boolean(repeat)} />
            <Toggle rect={ADD.google} label={label('googleCalendar')} on={false} />

            {/* Alt çubuk */}
            <span style={{ left: 240, top: 618, width: 800 }} className="tw-absolute tw-h-px tw-bg-slate-200 dark:tw-bg-white/10" />
            <span style={abs(ADD.cancel)} className="tw-absolute tw-grid tw-place-items-center tw-rounded-full tw-text-sm tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{t('profilePage.common.cancel')}</span>
            <span style={abs(ADD.save)} className="tw-absolute tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-bg-gradient-to-b tw-from-teal-400 tw-to-cyan-600 tw-text-sm tw-font-semibold tw-text-white tw-shadow-lg tw-shadow-teal-500/30">
              {saving ? <LuLoaderCircle className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuSave className="tw-h-4 tw-w-4" />}
              {saving ? label('saving') : t('profilePage.common.save')}
            </span>

            {menu === 'project' && <Menu rect={ADD.project} options={PROJECT_KEYS.map((key) => PROJECTS[key].name)} hover={menuHover} />}
            {menu === 'patient' && <Menu rect={ADD.patient} options={PATIENTS} hover={menuHover} />}
            {menu === 'type' && <Menu rect={ADD.type} options={VISIT_TYPES.map((key) => t(`profilePage.calendar.visitTypes.${key}`))} hover={menuHover} />}
          </div>
        </>
      )}
    </>
  );
};

AddVisitScene.propTypes = { state: PropTypes.object.isRequired };

export default AddVisitScene;
